import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { DefaultLayout } from 'hellosign/common/layout';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import styles from './app-shell.module.scss';
import type { AppShellLayoutProps } from './create-app-shell-layout';

const InternalAppShellLayout = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-internal-app-shell" */ './internal/internal-app-shell-layout'
    ),
);
const PatchedOfficialAppShellLayout = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-patched-official-app-shell" */ './patched-official/patched-official-app-shell-layout'
    ),
);

type Props = AppShellLayoutProps &
  Readonly<{
    /**
     * The layout the page renders when the internal AppShell flag is off, so a
     * failed shell returns the page to it.
     */
    fallbackLayout?: React.ElementType<React.PropsWithChildren<{}>>;
  }>;

const messages = defineMessages({
  loading: {
    id: '',
    description:
      'Accessible label for the placeholder shown while the page layout loads',
    defaultMessage: 'Loading page',
  },
});

type BoundaryState = {
  failed: boolean;
};

type BoundaryProps = React.PropsWithChildren<{
  pageContent: React.ReactNode;
  fallbackLayout: React.ElementType<React.PropsWithChildren<{}>>;
}>;

class AppShellBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { failed: false };

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      const { fallbackLayout: FallbackLayout, pageContent } = this.props;
      // Re-rendering this.props.children would remount the failed implementation.
      return <FallbackLayout>{pageContent}</FallbackLayout>;
    }

    return this.props.children;
  }
}

export function AppShellLoading() {
  const intl = useIntl();

  return (
    <div
      className={styles.appShellLoading}
      role="status"
      aria-label={intl.formatMessage(messages.loading)}
      aria-busy="true"
    />
  );
}

/**
 * Callers render this only when the internal AppShell flag is on. This component
 * loads one implementation chunk: the patched official flag selects which one.
 */
export function AppShell({
  children,
  fallbackLayout = DefaultLayout,
  selectedNavigationItem,
  withNavigation,
}: Props) {
  const usePatchedOfficialAppShell = useFeatureFlag(
    'sign_core_2026_10_12_patched_official_app_shell',
  );
  const SelectedLayout = usePatchedOfficialAppShell
    ? PatchedOfficialAppShellLayout
    : InternalAppShellLayout;

  return (
    <AppShellBoundary pageContent={children} fallbackLayout={fallbackLayout}>
      <React.Suspense
        fallback={
          // Rendering DefaultLayout here would flash the legacy navigation on reload.
          <AppShellLoading />
        }
      >
        <SelectedLayout
          selectedNavigationItem={selectedNavigationItem}
          withNavigation={withNavigation}
        >
          {children}
        </SelectedLayout>
      </React.Suspense>
    </AppShellBoundary>
  );
}
